import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeader, LoadingTable, ErrorState } from '../components/States';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuth } from '../context/AuthContext';
import * as api from '../lib/api';
import { formatCurrency } from '../lib/format';
import { ArrowLeft, Calculator, Upload, Trash2, Lock, ArrowUp, ArrowDown, AlertTriangle, CheckCircle2, Sparkles, FileSpreadsheet, FileText } from 'lucide-react';

const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

// Voci di COSTO: uno scostamento positivo (spesa maggiore) è sfavorevole → colore invertito
const COST_ROW_KEYS = new Set([
  'rimanenze_iniziali', 'acquisti', 'costo_materie_prime', 'costi_produttivi', 'costo_venduto',
  'costo_personale', 'costi_commerciali', 'costi_amministrativi', 'ammortamenti',
  'oneri_tributari', 'oneri_finanziari', 'costi_extra', 'imposte',
]);

function fileToB64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function AnalisiCGDettaglio() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [files, setFiles] = useState([]);
  const [delta, setDelta] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [computing, setComputing] = useState(false);
  const [upCompany, setUpCompany] = useState('');
  const [uploading, setUploading] = useState(false);
  const [adjustments, setAdjustments] = useState([]);
  const [schemaRows, setSchemaRows] = useState([]);
  const [adjForm, setAdjForm] = useState({ company_id: '', type: 'sal_competenze', target_row_key: 'ricavi', amount: '', sign: 1, note: '' });
  const [savingAdj, setSavingAdj] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const a = await api.cgaAnalysisGet(id);
      setAnalysis(a);
      const [comp, vers] = await Promise.all([api.cgaCompanies(), api.cgaAnalysisVersions(id)]);
      setCompanies(comp); setVersions(vers);
      if (a.company_ids?.length) { setUpCompany(a.company_ids[0]); setAdjForm((f) => ({ ...f, company_id: f.company_id || a.company_ids[0] })); }
      const fileLists = await Promise.all((a.company_ids || []).map((cid) => api.cgaFiles({ company_id: cid })));
      setFiles(fileLists.flat());
      try { setAdjustments(await api.cgaAdjustmentsList(id)); } catch (_) { setAdjustments([]); }
      try {
        const schemas = await api.cgaSchemas();
        const sc = schemas.find((s) => s.id === a.schema_id);
        setSchemaRows((sc?.rows || []).filter((r) => !r.computed));
      } catch (_) { setSchemaRows([]); }
      if (vers.length) { try { setDelta(await api.cgaAnalysisDelta(id)); } catch (_) { setDelta(null); } }
    } catch (e) { setErr(api.apiError(e)); } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const compById = (cid) => companies.find((c) => c.id === cid);
  const locked = analysis?.locked || analysis?.is_baseline;
  const canCompute = user?.role === 'admin' || user?.role === 'control' || !locked;

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !upCompany) { toast.error('Seleziona una società'); return; }
    setUploading(true);
    try {
      const content_b64 = await fileToB64(file);
      const res = await api.cgaFileUpload({ company_id: upCompany, filename: file.name, content_b64, kind: 'ledger',
        period: { year: analysis.year, from_month: analysis.from_month, to_month: analysis.to_month } });
      toast.success(`Caricato: ${res.items_count} righe (${res.parse_status})`);
      load();
    } catch (e2) { toast.error(api.apiError(e2)); } finally { setUploading(false); }
  }
  async function delFile(fid) {
    try { await api.cgaFileDelete(fid); toast.success('File eliminato'); load(); } catch (e) { toast.error(api.apiError(e)); }
  }
  async function compute() {
    setComputing(true);
    try { const v = await api.cgaAnalysisCompute(id); toast.success(`Calcolo completato (v${v.version_no})`); load(); }
    catch (e) { toast.error(api.apiError(e)); } finally { setComputing(false); }
  }

  const ADJ_PRESETS = {
    sal_competenze: { target_row_key: 'ricavi', sign: 1, label: 'SAL cantieri e competenze' },
    magazzino: { target_row_key: 'rimanenze_finali', sign: 1, label: 'Magazzino / Rimanenze' },
    altro: { target_row_key: 'ricavi', sign: 1, label: 'Rettifica di periodo' },
  };
  function onAdjTypeChange(t) {
    const p = ADJ_PRESETS[t] || ADJ_PRESETS.altro;
    setAdjForm((f) => ({ ...f, type: t, target_row_key: p.target_row_key, sign: p.sign }));
  }
  async function addAdjustment() {
    if (!adjForm.company_id || !adjForm.target_row_key || !adjForm.amount) { toast.error('Compila società, voce e importo'); return; }
    setSavingAdj(true);
    try {
      await api.cgaAdjustmentCreate(id, {
        company_id: adjForm.company_id, type: adjForm.type, target_row_key: adjForm.target_row_key,
        label: ADJ_PRESETS[adjForm.type]?.label, amount: parseFloat(adjForm.amount), sign: Number(adjForm.sign), note: adjForm.note || null,
      });
      toast.success('Rettifica aggiunta — ricalcola per applicarla');
      setAdjForm((f) => ({ ...f, amount: '', note: '' }));
      setAdjustments(await api.cgaAdjustmentsList(id));
    } catch (e) { toast.error(api.apiError(e)); } finally { setSavingAdj(false); }
  }
  async function delAdjustment(adjId) {
    try { await api.cgaAdjustmentDelete(adjId); setAdjustments(await api.cgaAdjustmentsList(id)); toast.success('Rettifica rimossa — ricalcola per aggiornare'); }
    catch (e) { toast.error(api.apiError(e)); }
  }
  async function exportFile(format) {
    try {
      const blob = await api.cgaAnalysisExport(id, format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `consolidato_${(analysis.name || 'analisi').replace(/[^a-z0-9]/gi, '_')}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} generato`);
    } catch (e) {
      toast.error('Export non riuscito: esegui prima il calcolo');
    }
  }

  const [reportLoading, setReportLoading] = useState(false);
  async function exportReport() {
    setReportLoading(true);
    try {
      const blob = await api.cgaAnalysisReport(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_direzione_${(analysis.name || 'analisi').replace(/[^a-z0-9]/gi, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report direzione generato');
    } catch (e) {
      toast.error('Report non riuscito: esegui prima il calcolo');
    } finally { setReportLoading(false); }
  }

  if (loading) return <div><PageHeader title="Analisi" /><LoadingTable /></div>;
  if (err) return <div><PageHeader title="Analisi" /><ErrorState message={err} onRetry={load} /></div>;

  return (
    <div data-testid="cga-dettaglio-page">
      <button className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3" onClick={() => nav('/analisi-cg/analisi')} data-testid="back-btn"><ArrowLeft className="h-4 w-4" />Torna alle analisi</button>
      <PageHeader
        title={<span className="flex items-center gap-2">{analysis.name}{locked && <Badge variant="secondary"><Lock className="h-3 w-3 mr-1" />Base bloccata</Badge>}</span>}
        description={`Anno ${analysis.year} · ${MONTHS[analysis.from_month - 1]}–${MONTHS[analysis.to_month - 1]} · ${(analysis.company_ids || []).length} società`}
        actions={<Button onClick={compute} disabled={computing || !canCompute} data-testid="compute-btn"><Calculator className="h-4 w-4 mr-1.5" />{computing ? 'Calcolo…' : 'Calcola'}</Button>}
      />
      {!canCompute && <Alert className="mb-4"><Lock className="h-4 w-4" /><AlertDescription>Analisi Base bloccata: il ricalcolo è riservato all'amministratore.</AlertDescription></Alert>}

      <Tabs defaultValue="data">
        <TabsList>
          <TabsTrigger value="data" data-testid="tab-data">Dati & Calcolo</TabsTrigger>
          <TabsTrigger value="delta" data-testid="tab-delta">Consolidato & Delta</TabsTrigger>
          <TabsTrigger value="adjust" data-testid="tab-adjust">Rettifiche di periodo</TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai">Analisi AI</TabsTrigger>
          <TabsTrigger value="versions" data-testid="tab-versions">Versioni</TabsTrigger>
        </TabsList>

        <TabsContent value="data" className="mt-4 space-y-4">
          <Card className="p-4">
            <h3 className="text-sm font-semibold mb-3">Mastrini della Base 2025 ({files.length})</h3>
            <p className="text-xs text-muted-foreground mb-3">I mastrini 2025 sono inizializzati automaticamente dal sistema. Elenco delle fonti dati per società (sola lettura).</p>
            {files.length === 0 ? <p className="text-sm text-muted-foreground">Nessun mastrino disponibile.</p> : (
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40"><tr>
                    <th className="text-left p-2 text-xs font-semibold text-muted-foreground">Società</th>
                    <th className="text-left p-2 text-xs font-semibold text-muted-foreground">File</th>
                    <th className="text-left p-2 text-xs font-semibold text-muted-foreground">Formato</th>
                    <th className="text-right p-2 text-xs font-semibold text-muted-foreground">Righe</th>
                    <th className="text-left p-2 text-xs font-semibold text-muted-foreground">Stato</th>
                  </tr></thead>
                  <tbody>
                    {files.map((f) => (
                      <tr key={f.id} className="border-t" data-testid="cga-file-row">
                        <td className="p-2">{f.company_code}</td>
                        <td className="p-2">{f.filename}</td>
                        <td className="p-2 uppercase text-xs">{f.format}</td>
                        <td className="p-2 text-right tabular-nums">{f.items_count}</td>
                        <td className="p-2"><Badge variant={f.parse_status === 'ok' ? 'secondary' : f.parse_status === 'partial' ? 'outline' : 'destructive'}>{f.parse_status}</Badge>{f.ai_assisted && <Badge variant="outline" className="ml-1.5"><Sparkles className="h-3 w-3 mr-1" />AI</Badge>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="delta" className="mt-4 space-y-4">
          {!delta ? (
            <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="delta-empty">Nessun calcolo disponibile. Premi “Calcola” per generare il consolidato.</Card>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge variant="outline">Corrente: {delta.labels.current}</Badge>
                  {delta.has_prev && <Badge variant="outline">Prec.: {delta.labels.prev}</Badge>}
                  {delta.has_baseline && <Badge variant="outline">{delta.labels.baseline}</Badge>}
                  {delta.has_budget && <Badge variant="outline">{delta.labels.budget}</Badge>}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => exportFile('xlsx')} data-testid="export-xlsx-btn"><FileSpreadsheet className="h-4 w-4 mr-1.5" />Esporta Excel</Button>
                  <Button variant="outline" size="sm" onClick={() => exportFile('pdf')} data-testid="export-pdf-btn"><FileText className="h-4 w-4 mr-1.5" />Esporta PDF</Button>
                  <Button size="sm" onClick={exportReport} disabled={reportLoading} data-testid="export-report-btn"><FileText className="h-4 w-4 mr-1.5" />{reportLoading ? 'Genero…' : 'Report Direzione'}</Button>
                </div>
              </div>
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="delta-table">
                    <thead className="bg-muted/40"><tr>
                      <th className="text-left p-2.5 text-xs font-semibold text-muted-foreground">Voce</th>
                      <th className="text-right p-2.5 text-xs font-semibold text-muted-foreground">Corrente</th>
                      {delta.has_prev && <th className="text-right p-2.5 text-xs font-semibold text-muted-foreground">Periodo prec.</th>}
                      {delta.has_prev && <th className="text-right p-2.5 text-xs font-semibold text-muted-foreground">Δ prec.</th>}
                      <th className="text-right p-2.5 text-xs font-semibold text-muted-foreground">Base (pro-rata)</th>
                      <th className="text-right p-2.5 text-xs font-semibold text-muted-foreground">Δ base</th>
                      {delta.has_budget && <th className="text-right p-2.5 text-xs font-semibold text-muted-foreground">Budget (pro-rata)</th>}
                      {delta.has_budget && <th className="text-right p-2.5 text-xs font-semibold text-muted-foreground">Δ budget</th>}
                    </tr></thead>
                    <tbody>
                      {delta.rows.map((r) => (
                        <tr key={r.key} className={`border-t ${r.computed ? 'bg-muted/20 font-semibold' : ''}`}>
                          <td className="p-2.5">{r.label}</td>
                          <td className="p-2.5 text-right tabular-nums">{formatCurrency(r.current)}</td>
                          {delta.has_prev && <td className="p-2.5 text-right tabular-nums text-muted-foreground">{r.prev != null ? formatCurrency(r.prev) : '—'}</td>}
                          {delta.has_prev && <td className="p-2.5 text-right tabular-nums"><DeltaCell d={r.delta_prev} pct={r.delta_prev_pct} invert={COST_ROW_KEYS.has(r.key)} /></td>}
                          <td className="p-2.5 text-right tabular-nums text-muted-foreground">{r.baseline_prorata != null ? formatCurrency(r.baseline_prorata) : '—'}</td>
                          <td className="p-2.5 text-right tabular-nums"><DeltaCell d={r.delta_baseline} pct={r.delta_baseline_pct} invert={COST_ROW_KEYS.has(r.key)} /></td>
                          {delta.has_budget && <td className="p-2.5 text-right tabular-nums text-muted-foreground">{r.budget_prorata != null ? formatCurrency(r.budget_prorata) : '—'}</td>}
                          {delta.has_budget && <td className="p-2.5 text-right tabular-nums"><DeltaCell d={r.delta_budget} pct={r.delta_budget_pct} invert={COST_ROW_KEYS.has(r.key)} /></td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
              <Reconciliation analysisId={id} />
            </>
          )}
        </TabsContent>

        <TabsContent value="adjust" className="mt-4 space-y-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Aggiungi le <strong>rettifiche di competenza</strong> del periodo per normalizzare il confronto con la Base:
              <strong> SAL cantieri e competenze</strong> (ricavi maturati non ancora fatturati) e <strong>magazzino/rimanenze</strong>.
              Per il <strong>magazzino</strong> inserisci il valore di <strong>chiusura del periodo</strong>: il sistema imputa automaticamente le
              Rimanenze iniziali = chiusura del periodo precedente, così a Conto Economico incide solo la <strong>variazione</strong> (finali − iniziali), non il valore assoluto.
              Dopo aver aggiunto o rimosso una rettifica, premi <em>Calcola</em> per applicarla.
            </AlertDescription>
          </Alert>

          <Card className="p-4" data-testid="adjustment-form">
            <h3 className="text-sm font-semibold mb-3">Nuova rettifica di periodo</h3>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
              <div className="md:col-span-1"><Label className="text-xs">Tipo</Label>
                <Select value={adjForm.type} onValueChange={onAdjTypeChange}>
                  <SelectTrigger data-testid="adj-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sal_competenze">SAL / competenze</SelectItem>
                    <SelectItem value="magazzino">Magazzino / Rimanenze</SelectItem>
                    <SelectItem value="altro">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-1"><Label className="text-xs">Società</Label>
                <Select value={adjForm.company_id} onValueChange={(v) => setAdjForm((f) => ({ ...f, company_id: v }))}>
                  <SelectTrigger data-testid="adj-company"><SelectValue placeholder="Società" /></SelectTrigger>
                  <SelectContent>{(analysis.company_ids || []).map((cid) => <SelectItem key={cid} value={cid}>{compById(cid)?.code || cid}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="md:col-span-1"><Label className="text-xs">Voce (riga schema)</Label>
                <Select value={adjForm.target_row_key} onValueChange={(v) => setAdjForm((f) => ({ ...f, target_row_key: v }))}>
                  <SelectTrigger data-testid="adj-target"><SelectValue /></SelectTrigger>
                  <SelectContent>{schemaRows.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="md:col-span-1"><Label className="text-xs">Segno</Label>
                <Select value={String(adjForm.sign)} onValueChange={(v) => setAdjForm((f) => ({ ...f, sign: Number(v) }))}>
                  <SelectTrigger data-testid="adj-sign"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">+ (aumenta)</SelectItem>
                    <SelectItem value="-1">− (riduce)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-1"><Label className="text-xs">Importo (€)</Label>
                <Input type="number" step="0.01" value={adjForm.amount} onChange={(e) => setAdjForm((f) => ({ ...f, amount: e.target.value }))} placeholder="0,00" data-testid="adj-amount" />
              </div>
              <div className="md:col-span-1">
                <Button className="w-full" onClick={addAdjustment} disabled={savingAdj} data-testid="adj-add-btn">{savingAdj ? 'Aggiungo…' : 'Aggiungi'}</Button>
              </div>
            </div>
            <div className="mt-3"><Label className="text-xs">Nota (facoltativa)</Label>
              <Input value={adjForm.note} onChange={(e) => setAdjForm((f) => ({ ...f, note: e.target.value }))} placeholder="es. SAL cantiere Rossi al 30/06, magazzino da inventario provvisorio" data-testid="adj-note" />
            </div>
          </Card>

          <Card className="p-0 overflow-hidden">
            <div className="p-3 border-b bg-muted/30 flex items-center justify-between">
              <h3 className="text-sm font-semibold">Rettifiche applicate ({adjustments.length})</h3>
              {adjustments.length > 0 && <Button size="sm" onClick={compute} disabled={computing} data-testid="adj-recompute-btn"><Calculator className="h-4 w-4 mr-1.5" />{computing ? 'Calcolo…' : 'Ricalcola'}</Button>}
            </div>
            {adjustments.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">Nessuna rettifica di periodo. Aggiungi SAL/competenze e magazzino per normalizzare il confronto.</p>
            ) : (
              <table className="w-full text-sm" data-testid="adjustments-table">
                <thead className="bg-muted/40"><tr>
                  <th className="text-left p-2 text-xs font-semibold text-muted-foreground">Tipo</th>
                  <th className="text-left p-2 text-xs font-semibold text-muted-foreground">Società</th>
                  <th className="text-left p-2 text-xs font-semibold text-muted-foreground">Voce</th>
                  <th className="text-right p-2 text-xs font-semibold text-muted-foreground">Importo</th>
                  <th className="text-left p-2 text-xs font-semibold text-muted-foreground">Nota</th>
                  <th className="p-2"></th>
                </tr></thead>
                <tbody>
                  {adjustments.map((a) => {
                    const rowLabel = schemaRows.find((r) => r.key === a.target_row_key)?.label || a.target_row_key;
                    return (
                      <tr key={a.id} className="border-t" data-testid="adjustment-row">
                        <td className="p-2">{a.type === 'sal_competenze' ? 'SAL / competenze' : a.type === 'magazzino' ? 'Magazzino' : 'Altro'}</td>
                        <td className="p-2 font-medium">{a.company_code}</td>
                        <td className="p-2">{rowLabel}</td>
                        <td className="p-2 text-right tabular-nums">{a.sign < 0 ? '−' : '+'}{formatCurrency(Math.abs(a.amount))}</td>
                        <td className="p-2 text-xs text-muted-foreground">{a.note || '—'}</td>
                        <td className="p-2 text-right"><Button variant="ghost" size="sm" onClick={() => delAdjustment(a.id)} data-testid="adj-del-btn"><Trash2 className="h-4 w-4" /></Button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Card>
        </TabsContent>


        <TabsContent value="ai" className="mt-4">
          <AIPanel analysisId={id} hasVersion={versions.length > 0} />
        </TabsContent>

        <TabsContent value="versions" className="mt-4">
          <Card className="p-4">
            {versions.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna versione calcolata.</p> : (
              <div className="space-y-2">
                {versions.map((v) => (
                  <div key={v.id} className="flex items-center justify-between rounded-lg border p-3 text-sm" data-testid="version-row">
                    <div className="flex items-center gap-3"><Badge variant="outline">v{v.version_no}</Badge><span className="text-muted-foreground">{new Date(v.computed_at).toLocaleString('it-IT')}</span></div>
                    <span className="text-xs text-muted-foreground font-mono">#{v.inputs_hash}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AIPanel({ analysisId, hasVersion }) {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  async function ask() {
    setLoading(true); setAnswer('');
    try {
      const res = await api.cgaAnalysisAI(analysisId, question.trim());
      setAnswer(res.answer);
    } catch (e) { toast.error(api.apiError(e)); } finally { setLoading(false); }
  }
  if (!hasVersion) return <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="ai-empty">Esegui prima il calcolo (Calcola) per poter analizzare il consolidato con l'AI.</Card>;
  return (
    <Card className="p-4" data-testid="ai-panel">
      <div className="flex items-center gap-2 mb-3"><Sparkles className="h-4 w-4" /><h3 className="text-sm font-semibold">Commento AI sugli scostamenti</h3></div>
      <Textarea rows={3} placeholder="Domanda opzionale (es. Perché il MOL è calato rispetto alla Base?). Lascia vuoto per un commento generale." value={question} onChange={(e) => setQuestion(e.target.value)} data-testid="ai-question-input" />
      <div className="mt-3">
        <Button onClick={ask} disabled={loading} data-testid="ai-ask-btn"><Sparkles className="h-4 w-4 mr-1.5" />{loading ? 'Analisi in corso…' : 'Genera commento'}</Button>
      </div>
      {answer && (
        <div className="mt-4 rounded-lg border bg-muted/30 p-4 text-sm whitespace-pre-wrap leading-relaxed" data-testid="ai-answer">{answer}</div>
      )}
    </Card>
  );
}

function DeltaCell({ d, pct, invert = false }) {
  if (d == null) return <span className="text-muted-foreground">—</span>;
  const good = invert ? d <= 0 : d >= 0;
  const cls = d === 0 ? 'text-muted-foreground' : good ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--danger))]';
  const up = d > 0;
  return <span className={`inline-flex items-center gap-1 ${cls}`}>{d !== 0 && (up ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}{formatCurrency(d)}{pct != null && <span className="text-[10px]">({pct}%)</span>}</span>;
}

function Reconciliation({ analysisId }) {
  const [rec, setRec] = useState(null);
  useEffect(() => {
    let ok = true;
    api.cgaAnalysisVersions(analysisId).then(async (vs) => {
      if (!vs.length) return;
      const full = await api.cgaAnalysisGet(analysisId);
      const res = await api.cgaConsolidate({ schema_id: full.schema_id, company_ids: full.company_ids, year: full.year, eliminate: full.eliminate });
      if (ok) setRec(res);
    }).catch(() => {});
    return () => { ok = false; };
  }, [analysisId]);
  if (!rec) return null;
  return (
    <Card className="p-4" data-testid="reconciliation-card">
      <h3 className="text-sm font-semibold mb-3">Riconciliazione partite intercompany</h3>
      {rec.warnings?.length > 0 && rec.warnings.map((w, i) => (
        <Alert key={i} className="mb-2"><AlertTriangle className="h-4 w-4" /><AlertDescription>{w}</AlertDescription></Alert>
      ))}
      {rec.reconciliation.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna partita intercompany rilevata.</p> : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40"><tr>
              <th className="text-left p-2 text-xs font-semibold text-muted-foreground">Coppia</th>
              <th className="text-right p-2 text-xs font-semibold text-muted-foreground">Lato A</th>
              <th className="text-right p-2 text-xs font-semibold text-muted-foreground">Lato B</th>
              <th className="text-right p-2 text-xs font-semibold text-muted-foreground">Differenza</th>
              <th className="text-left p-2 text-xs font-semibold text-muted-foreground">Quadratura</th>
            </tr></thead>
            <tbody>
              {rec.reconciliation.map((r, i) => (
                <tr key={i} className="border-t" data-testid="reconciliation-row">
                  <td className="p-2">{r.company_a_code} ↔ {r.company_b_code}</td>
                  <td className="p-2 text-right tabular-nums">{formatCurrency(r.a_side_total)}</td>
                  <td className="p-2 text-right tabular-nums">{formatCurrency(r.b_side_total)}</td>
                  <td className={`p-2 text-right tabular-nums ${r.squared ? 'text-muted-foreground' : 'text-[hsl(var(--danger))]'}`}>{formatCurrency(r.difference)}</td>
                  <td className="p-2">{r.squared ? <span className="inline-flex items-center gap-1 text-[hsl(var(--success))]"><CheckCircle2 className="h-4 w-4" />Quadra</span> : <span className="inline-flex items-center gap-1 text-[hsl(var(--danger))]"><AlertTriangle className="h-4 w-4" />Da verificare</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
